/**
 * GroupSetupView
 *
 * Host setup interface for configuring a group prioritization session.
 * - Displays options list with inline "Add option" form
 * - Shows + edits weighted criteria
 * - Invite link copy
 * - "Open Session" CTA (host only)
 *
 * Non-host members see a read-only view + waiting banner.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useGroupSessionStore } from '../../stores/prioritization/groupSessionStore';
import type { GroupCriterion } from '../../types/prioritization/groupSession';
import { GroupDecisionFramingPanel, useGroupPageToast } from './GroupDecisionFramingPanel';
import { GuestLinkControl } from './GuestLinkControl';
import { OPEN_FOR_PARTICIPANTS_LABEL } from '@/lib/sessions/vocabulary';

// ─── Props ────────────────────────────────────────────────────────────────────

interface GroupSetupViewProps {
  sessionId: string;
  isHost: boolean;
}

// ─── Styles (shared tokens) ────────────────────────────────────────────────────

const card: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--rule)',
  borderRadius: '10px',
  padding: '16px',
  marginBottom: '8px',
};

const sectionLabel: React.CSSProperties = {
  fontFamily: 'var(--mono)',
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--text-ter)',
  marginBottom: '10px',
  display: 'block',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--bg-raised)',
  border: '1px solid var(--rule-bright)',
  borderRadius: '6px',
  color: 'var(--text-pri)',
  fontFamily: 'var(--sans)',
  fontSize: '14px',
  padding: '9px 12px',
  outline: 'none',
  boxSizing: 'border-box',
};

const primaryBtn: React.CSSProperties = {
  background: 'var(--mustard)',
  color: '#0B0F19',
  border: 'none',
  borderRadius: '8px',
  fontFamily: 'var(--sans)',
  fontSize: '14px',
  fontWeight: 700,
  padding: '10px 20px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const ghostBtn: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--rule-bright)',
  borderRadius: '8px',
  color: 'var(--text-sec)',
  fontFamily: 'var(--sans)',
  fontSize: '13px',
  padding: '7px 14px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const iconBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'var(--text-ter)',
  cursor: 'pointer',
  fontFamily: 'var(--mono)',
  fontSize: '13px',
  padding: '4px 8px',
  borderRadius: '4px',
  lineHeight: 1,
};

// ─── Add Option Form ──────────────────────────────────────────────────────────

function AddOptionForm({ onAdd }: { onAdd: (title: string, description: string) => void }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [showDesc, setShowDesc] = useState(false);
  const titleRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const t = title.trim();
      if (!t) return;
      onAdd(t, description.trim());
      setTitle('');
      setDescription('');
      setShowDesc(false);
    },
    [title, description, onAdd],
  );

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        background: 'var(--bg-raised)',
        border: '1px solid var(--mustard)',
        borderRadius: '10px',
        padding: '14px',
        marginBottom: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      <input
        ref={titleRef}
        style={inputStyle}
        placeholder="Option title (required)"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={160}
        required
      />
      {showDesc ? (
        <textarea
          style={{ ...inputStyle, resize: 'vertical', minHeight: '72px' }}
          placeholder="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      ) : (
        <button
          type="button"
          onClick={() => setShowDesc(true)}
          style={{ ...ghostBtn, alignSelf: 'flex-start', fontSize: '12px' }}
        >
          + Add description
        </button>
      )}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button
          type="submit"
          style={{ ...primaryBtn, padding: '8px 18px', fontSize: '13px' }}
          disabled={!title.trim()}
        >
          Add Option
        </button>
      </div>
    </form>
  );
}

// ─── Criteria Editor ──────────────────────────────────────────────────────────

interface DraftCriterion {
  id: string;
  title: string;
  description: string;
  weight: number;
}

function CriteriaEditor({
  initial,
  onSave,
  onCancel,
}: {
  initial: GroupCriterion[];
  onSave: (drafts: DraftCriterion[]) => void;
  onCancel: () => void;
}) {
  const [drafts, setDrafts] = useState<DraftCriterion[]>(() =>
    initial.length > 0
      ? initial.map((c) => ({ id: c.id, title: c.title, description: c.description ?? '', weight: c.weight }))
      : [{ id: `new-${Date.now()}`, title: '', description: '', weight: 1 }],
  );

  const update = useCallback((id: string, field: keyof DraftCriterion, value: string | number) => {
    setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, [field]: value } : d)));
  }, []);

  const add = useCallback(() => {
    setDrafts((prev) => [
      ...prev,
      { id: `new-${Date.now()}-${Math.random()}`, title: '', description: '', weight: 1 },
    ]);
  }, []);

  const remove = useCallback((id: string) => {
    setDrafts((prev) => prev.filter((d) => d.id !== id));
  }, []);

  const move = useCallback((id: string, dir: -1 | 1) => {
    setDrafts((prev) => {
      const idx = prev.findIndex((d) => d.id === id);
      if (idx < 0) return prev;
      const next = [...prev];
      const swap = idx + dir;
      if (swap < 0 || swap >= next.length) return prev;
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next;
    });
  }, []);

  const totalWeight = drafts.reduce((s, d) => s + (Number(d.weight) || 0), 0) || 1;

  const handleSave = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const valid = drafts.filter((d) => d.title.trim());
      if (valid.length === 0) return;
      onSave(valid);
    },
    [drafts, onSave],
  );

  return (
    <form
      onSubmit={handleSave}
      style={{
        background: 'var(--bg-raised)',
        border: '1px solid var(--rule-bright)',
        borderRadius: '10px',
        padding: '16px',
        marginTop: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      {drafts.map((draft, idx) => {
        const pct = Math.round(((Number(draft.weight) || 0) / totalWeight) * 100);
        return (
          <div
            key={draft.id}
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--rule)',
              borderRadius: '8px',
              padding: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <input
                style={{ ...inputStyle, flex: 1 }}
                placeholder="Criterion title"
                value={draft.title}
                onChange={(e) => update(draft.id, 'title', e.target.value)}
                required
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <button
                  type="button"
                  style={iconBtn}
                  onClick={() => move(draft.id, -1)}
                  disabled={idx === 0}
                  title="Move up"
                >
                  ↑
                </button>
                <button
                  type="button"
                  style={iconBtn}
                  onClick={() => move(draft.id, 1)}
                  disabled={idx === drafts.length - 1}
                  title="Move down"
                >
                  ↓
                </button>
              </div>
              <button
                type="button"
                style={{ ...iconBtn, color: 'var(--coral)' }}
                onClick={() => remove(draft.id)}
                title="Remove"
                disabled={drafts.length === 1}
              >
                ✕
              </button>
            </div>

            <input
              style={inputStyle}
              placeholder="Description (optional)"
              value={draft.description}
              onChange={(e) => update(draft.id, 'description', e.target.value)}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label
                style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: 'var(--text-ter)', whiteSpace: 'nowrap' }}
              >
                Weight
              </label>
              <input
                type="number"
                min="1"
                max="100"
                style={{ ...inputStyle, width: '72px', flex: 'none' }}
                value={draft.weight}
                onChange={(e) => update(draft.id, 'weight', Number(e.target.value))}
              />
              <span
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: '12px',
                  color: 'var(--mustard)',
                  background: 'var(--mustard-dim)',
                  borderRadius: '4px',
                  padding: '2px 8px',
                }}
              >
                {pct}% of total
              </span>
            </div>
          </div>
        );
      })}

      <button type="button" onClick={add} style={{ ...ghostBtn, alignSelf: 'flex-start' }}>
        + Add criterion
      </button>

      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', paddingTop: '4px' }}>
        <button type="button" onClick={onCancel} style={ghostBtn}>
          Cancel
        </button>
        <button
          type="submit"
          style={{ ...primaryBtn, padding: '9px 20px' }}
          disabled={!drafts.some((d) => d.title.trim())}
        >
          Save Criteria
        </button>
      </div>
    </form>
  );
}

export function GroupSetupView({ sessionId, isHost }: GroupSetupViewProps) {
  const {
    currentSession,
    options,
    criteria,
    isLoading,
    addGroupOption,
    setGroupCriteria,
    openGroupSession,
  } = useGroupSessionStore();

  // Stable workspace ID for RPC calls
  const workspaceId = currentSession?.workspaceId ?? '';

  const [showAddOption, setShowAddOption] = useState(false);
  const [editingCriteria, setEditingCriteria] = useState(false);
  const [openingSession, setOpeningSession] = useState(false);
  const toast = useGroupPageToast();

  // ── Add option ─────────────────────────────────────────────────────────────
  const handleAddOption = useCallback(
    async (title: string, description: string) => {
      if (!currentSession) return;
      await addGroupOption(workspaceId, sessionId, title, description || undefined);
      setShowAddOption(false);
    },
    [currentSession, addGroupOption, workspaceId, sessionId],
  );

  // ── Save criteria ──────────────────────────────────────────────────────────
  const handleSaveCriteria = useCallback(
    async (drafts: DraftCriterion[]) => {
      if (!currentSession) return;
      const mapped = drafts.map((d, idx) => ({
        title: d.title.trim(),
        description: d.description.trim() || undefined,
        weight: Number(d.weight) || 1,
        position: idx,
      }));
      await setGroupCriteria(sessionId, mapped);
      setEditingCriteria(false);
    },
    [currentSession, setGroupCriteria, sessionId],
  );

  // ── Copy invite link ───────────────────────────────────────────────────────
  const handleCopyInvite = useCallback(async () => {
    if (!currentSession?.joinToken) return;
    const link = `${window.location.origin}/groups/join/${currentSession.joinToken}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.show('Invite link copied!');
    } catch {
      toast.show('Could not copy — try manually: ' + link);
    }
  }, [currentSession, toast]);

  // ── Open session ───────────────────────────────────────────────────────────
  const canOpen = options.length > 0 && criteria.length > 0 && !isLoading && !openingSession;
  const openDisabledReason = !options.length
    ? 'Add at least one option'
    : !criteria.length
    ? 'Add at least one criterion'
    : undefined;

  const handleOpen = useCallback(async () => {
    if (!canOpen) return;
    setOpeningSession(true);
    try {
      await openGroupSession(sessionId);
    } finally {
      setOpeningSession(false);
    }
  }, [canOpen, openGroupSession, sessionId]);

  const totalWeight = criteria.reduce((s, c) => s + c.weight, 0) || 1;

  // ─────────────────────────────────────────────────────────────────────────
  // READ-ONLY view for non-host members
  // ─────────────────────────────────────────────────────────────────────────
  if (!isHost) {
    return (
      <div>
        <div
          style={{
            background: 'var(--bg-raised)',
            border: '1px solid var(--rule-bright)',
            borderRadius: '8px',
            padding: '14px 16px',
            marginBottom: '24px',
            display: 'flex',
            gap: '10px',
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: '20px' }}>⏳</span>
          <span style={{ fontFamily: 'var(--sans)', fontSize: '14px', color: 'var(--text-sec)' }}>
            Waiting for the host to open scoring. Here's what's been set up so far.
          </span>
        </div>

        <SectionBlock label={`Options (${options.length})`}>
          {options.length === 0 ? (
            <Muted>No options yet.</Muted>
          ) : (
            options.map((opt) => (
              <div key={opt.id} style={card}>
                <span style={{ fontFamily: 'var(--sans)', fontSize: '14px', fontWeight: 600, color: 'var(--text-pri)' }}>
                  {opt.title}
                </span>
                {opt.description && (
                  <p style={{ margin: '4px 0 0', fontFamily: 'var(--sans)', fontSize: '13px', color: 'var(--text-ter)' }}>
                    {opt.description}
                  </p>
                )}
              </div>
            ))
          )}
        </SectionBlock>

        <SectionBlock label={`Criteria (${criteria.length})`}>
          {criteria.length === 0 ? (
            <Muted>No criteria yet.</Muted>
          ) : (
            criteria.map((c) => {
              const pct = Math.round((c.weight / totalWeight) * 100);
              return (
                <div key={c.id} style={{ ...card, flexDirection: 'row', alignItems: 'center', gap: '12px', display: 'flex' }}>
                  <span style={{ flex: 1, fontFamily: 'var(--sans)', fontSize: '14px', color: 'var(--text-pri)' }}>
                    {c.title}
                  </span>
                  <span
                    style={{
                      fontFamily: 'var(--mono)',
                      fontSize: '12px',
                      color: 'var(--mustard)',
                      background: 'var(--mustard-dim)',
                      borderRadius: '4px',
                      padding: '2px 8px',
                    }}
                  >
                    {pct}%
                  </span>
                </div>
              );
            })
          )}
        </SectionBlock>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // HOST view
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div>
      <GroupDecisionFramingPanel sessionId={sessionId} toast={toast} />
      {/* Toast */}
      {toast.message && (
        <div
          style={{
            position: 'fixed',
            bottom: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--surface)',
            border: '1px solid var(--rule-bright)',
            borderRadius: '8px',
            padding: '10px 20px',
            fontFamily: 'var(--sans)',
            fontSize: '14px',
            color: 'var(--text-pri)',
            zIndex: 9999,
            pointerEvents: 'none',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          }}
        >
          {toast.message}
        </div>
      )}

      {/* ── Options ─────────────────────────────────────────────────────────── */}
      <SectionBlock label={`Options (${options.length})`}>
        {options.map((opt) => (
          <div key={opt.id} style={card}>
            <span style={{ fontFamily: 'var(--sans)', fontSize: '14px', fontWeight: 600, color: 'var(--text-pri)' }}>
              {opt.title}
            </span>
            {opt.description && (
              <p style={{ margin: '4px 0 0', fontFamily: 'var(--sans)', fontSize: '13px', color: 'var(--text-ter)' }}>
                {opt.description}
              </p>
            )}
            {opt.url && (
              <a
                href={opt.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontFamily: 'var(--mono)', fontSize: '12px', color: 'var(--mustard)', wordBreak: 'break-all', marginTop: '4px' }}
              >
                {opt.url}
              </a>
            )}
          </div>
        ))}

        {showAddOption ? (
          <AddOptionForm
            onAdd={handleAddOption}
          />
        ) : (
          <button
            type="button"
            onClick={() => setShowAddOption(true)}
            style={{ ...ghostBtn, marginTop: options.length > 0 ? '4px' : '0' }}
          >
            + Add an option
          </button>
        )}
      </SectionBlock>

      <Divider />

      {/* ── Criteria ─────────────────────────────────────────────────────────── */}
      <SectionBlock label={`What Matters (${criteria.length} criteria)`}>
        {!editingCriteria && criteria.length > 0 && (
          <div style={{ marginBottom: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {criteria.map((c) => {
              const pct = Math.round((c.weight / totalWeight) * 100);
              return (
                <div
                  key={c.id}
                  style={{
                    ...card,
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '12px',
                    display: 'flex',
                  }}
                >
                  <span style={{ flex: 1, fontFamily: 'var(--sans)', fontSize: '14px', color: 'var(--text-pri)' }}>
                    {c.title}
                    {c.description && (
                      <span style={{ color: 'var(--text-ter)', fontWeight: 400, marginLeft: '8px', fontSize: '12px' }}>
                        — {c.description}
                      </span>
                    )}
                  </span>
                  <span
                    style={{
                      fontFamily: 'var(--mono)',
                      fontSize: '12px',
                      color: 'var(--mustard)',
                      background: 'var(--mustard-dim)',
                      borderRadius: '4px',
                      padding: '2px 8px',
                    }}
                  >
                    {pct}%
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {editingCriteria ? (
          <CriteriaEditor
            initial={criteria}
            onSave={handleSaveCriteria}
            onCancel={() => setEditingCriteria(false)}
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditingCriteria(true)}
            style={ghostBtn}
          >
            {criteria.length === 0 ? '+ Add criteria' : 'Edit criteria'}
          </button>
        )}
      </SectionBlock>

      <Divider />

      {/* ── Invite ────────────────────────────────────────────────────────────── */}
      <SectionBlock label="Members">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <p
            style={{
              fontFamily: 'var(--sans)',
              fontSize: '13px',
              color: 'var(--text-sec)',
              margin: 0,
              flex: 1,
            }}
          >
            Share the invite link so team members can join this session.
          </p>
          <button
            type="button"
            onClick={handleCopyInvite}
            disabled={!currentSession?.joinToken}
            style={{
              ...primaryBtn,
              padding: '8px 16px',
              fontSize: '13px',
              opacity: currentSession?.joinToken ? 1 : 0.4,
            }}
          >
            Copy Invite Link
          </button>
        </div>
      </SectionBlock>

      <Divider />

      {/* ── Guest link (account-less, migration 660) — self-contained, carries
          its own heading/status so it can also be reused outside this page. */}
      <div style={{ marginBottom: '24px' }}>
        <GuestLinkControl sessionId={sessionId} />
      </div>

      <Divider />

      {/* ── Open Session ──────────────────────────────────────────────────────── */}
      <div style={{ paddingTop: '8px' }}>
        <button
          type="button"
          onClick={handleOpen}
          disabled={!canOpen}
          title={openDisabledReason}
          style={{
            ...primaryBtn,
            width: '100%',
            padding: '14px',
            fontSize: '15px',
            opacity: canOpen ? 1 : 0.45,
            cursor: canOpen ? 'pointer' : 'not-allowed',
            position: 'relative',
          }}
        >
          {openingSession ? 'Opening…' : OPEN_FOR_PARTICIPANTS_LABEL}
        </button>
        {openDisabledReason && (
          <p
            style={{
              fontFamily: 'var(--sans)',
              fontSize: '12px',
              color: 'var(--text-ter)',
              textAlign: 'center',
              marginTop: '8px',
            }}
          >
            {openDisabledReason} to open scoring.
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Layout helpers ───────────────────────────────────────────────────────────

function SectionBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: '24px' }}>
      <span style={sectionLabel}>{label}</span>
      {children}
    </div>
  );
}

function Divider() {
  return (
    <hr
      style={{
        border: 'none',
        borderTop: '1px solid var(--rule)',
        marginBottom: '24px',
      }}
    />
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return (
    <p
      style={{
        fontFamily: 'var(--sans)',
        fontSize: '13px',
        color: 'var(--text-ter)',
        fontStyle: 'italic',
        margin: 0,
        padding: '8px 0',
      }}
    >
      {children}
    </p>
  );
}
